<script lang="ts">
  import { run } from "svelte/legacy";
  import { createDialog, melt } from "@melt-ui/svelte";
  import { fly } from "svelte/transition";
  import { tick, type Snippet } from "svelte";
  import Kbd from "./Kbd.svelte";
  import Button from "./Button.svelte";
  import { Cross } from "../icons/index.js";
  import { writable, type Writable } from "svelte/store";
  import type { ClassValue } from "svelte/elements";

  interface Props {
    position?: "left" | "right";
    open?: Writable<boolean>;
    trigger?: Snippet;
    title?: Snippet;
    description?: Snippet;
    children?: Snippet;
    actions?: Snippet;
    class?: ClassValue;
    triggerClass?: ClassValue;
    id?: string;
  }

  let {
    position = "right",
    open = $bindable(writable(false)),
    trigger,
    title,
    description,
    children,
    actions,
    class: className,
    triggerClass,
    id,
  }: Props = $props();

  let contentElement = $state<HTMLElement>();
  let hasHigherPriorityDialog = $state(false);

  const {
    elements: { trigger: triggerElement, overlay, content, title: titleElement, close, portalled },
  } = createDialog({
    open,
    // svelte-ignore state_referenced_locally
    ids: id
      ? {
          content: id,
        }
      : undefined,
  });

  // detect when a higher z-index dialog is present and yield focus to it
  function checkForHigherPriorityDialogs() {
    const dialogs = document.querySelectorAll('[role="dialog"]');
    let foundHigherPriority = false;

    dialogs.forEach((dialog) => {
      if (dialog === contentElement) return;
      const style = window.getComputedStyle(dialog);
      const zIndex = parseInt(style.zIndex, 10);
      // drawer uses z-70, so anything higher takes priority
      if (!isNaN(zIndex) && zIndex > 70) {
        foundHigherPriority = true;
      }
    });

    hasHigherPriorityDialog = foundHigherPriority;
  }

  function observeHigherPriorityDialogs() {
    if (typeof document === "undefined") return;

    // run initial check
    checkForHigherPriorityDialogs();

    const observer = new MutationObserver(() => {
      checkForHigherPriorityDialogs();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["role", "class", "style"],
    });

    return () => observer.disconnect();
  }

  $effect(() => {
    if ($open) {
      return observeHigherPriorityDialogs();
    }
  });

  async function focusFirstElement() {
    await tick();

    // don't steal focus if a higher priority dialog is open
    if (hasHigherPriorityDialog) return;

    const selectors = [
      'input:not([disabled]):not([readonly]):not([type="hidden"])',
      "select:not([disabled])",
      "textarea:not([disabled]):not([readonly])",
      'button:not([disabled]):not([aria-label="close"])',
      '[tabindex]:not([tabindex="-1"])',
      "a[href]",
    ];

    for (const selector of selectors) {
      const focusable = contentElement?.querySelector(selector) as HTMLElement;
      if (focusable) {
        focusable.focus();
        break;
      }
    }
  }

  function drawerKeyboardShortcuts(node: HTMLElement) {
    function handleKeydown(event: KeyboardEvent) {
      if (!$open || hasHigherPriorityDialog) return;

      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        const form = node.querySelector("form") as HTMLFormElement;
        if (form) {
          form.requestSubmit();
        }
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        open.set(false);
      }
    }

    node.addEventListener("keydown", handleKeydown);

    return {
      destroy() {
        node.removeEventListener("keydown", handleKeydown);
      },
    };
  }
  run(() => {
    if ($open && contentElement) {
      focusFirstElement();
    }
  });
</script>

{#if trigger}
  <div {...$triggerElement} use:$triggerElement.action class={triggerClass}>
    {@render trigger()}
  </div>
{/if}

{#if $open}
  <div class="font-sans text-foreground-primary" {...$portalled} use:$portalled.action>
    <div
      {...$overlay} use:$overlay.action
      class="fixed inset-0 z-[70] bg-modal-overlay opacity-0 transition-opacity duration-[150ms] ease-linear"
      class:opacity-100={open}
    ></div>
    <div
      bind:this={contentElement}
      {...$content} use:$content.action
      use:drawerKeyboardShortcuts
      inert={hasHigherPriorityDialog}
      transition:fly={{
        x: position === "right" ? 350 : -350,
        duration: 200,
      }}
      class={[
        "fixed top-2 z-[70] flex h-[calc(100vh-16px)] w-full max-w-[min(90vw,420px)] flex-col overflow-hidden rounded-xl border border-border bg-mono-000 p-5 pt-4",
        position === "right" ? "right-2" : "left-2",
        className,
      ]}
    >
      <div class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between">
          <h2 {...$titleElement} use:$titleElement.action class="min-w-0 flex-1 shrink text-lg tracking-[-0.02em]">
            {@render title?.()}
          </h2>
          <div class="flex items-center gap-2">
            <Button
              meltElement={close}
              aria-label="close"
              shape="square"
              variant="tertiary"
              size="sm"
              class="text-subtle"
            >
              <Cross />
            </Button>
            <Kbd centerXHeight>esc</Kbd>
          </div>
        </div>
        {#if description}
          {@render description?.()}
        {/if}
      </div>

      <section class="mt-3 w-full flex-1 overflow-y-auto px-0.5">
        {@render children?.()}
      </section>

      <div class="relative pt-4">
        <div
          class="pointer-events-none absolute inset-x-0 -top-8 h-8 bg-gradient-to-b from-transparent to-mono-000"
        ></div>
        <div class="flex justify-end gap-2">
          {@render actions?.()}
        </div>
      </div>
    </div>
  </div>
{/if}
